import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import {
  AiChatInsertPayload,
  AiChatService,
  AiChatStore,
  AiChatSubscribePayload,
  ChatDocumentReferenceService,
  ChatDocumentReferenceStore,
  DocumentScanGate,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { WsErrorSuppressionService } from '@qtek/libs/ws-error-handler';
import {
  AccessLevel,
  AiChatEvent,
  ChatStatusPayload,
  ChatStatusPendingApprovalPayload,
  ChatStreamPayload,
  DocumentAttribute,
  DocumentReferenceEntityType,
  ChatStatusActionCardPayload,
  isActionCardStatus,
  isPendingApprovalStatus,
} from '@qtek/shared/models';
import {
  ALLOWED_DOCUMENT_ATTACHMENT_ACCEPT,
  ALLOWED_DOCUMENT_ATTACHMENT_FORMAT_ERROR_MESSAGE,
  buildDocumentReferenceFormData,
  isAllowedDocumentAttachment,
} from '@qtek/shared/utils';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import {
  filter,
  map,
  take,
  takeUntil,
  takeWhile,
  timeout,
} from 'rxjs/operators';
import {
  DynamicActionCardPayload,
  isDynamicActionCardPayload,
} from '../../models/action-card.types';
import { Author, ChatMsgVM } from '../../models/message.types';
import { ChatFileVM, processingStep } from '../../models/chat-file';
import { appendStreamedActionCard } from '../../models/action-card-stream';
import { findPromptForBubble } from '../../models/message.utils';
import {
  AiChatApprovalCardComponent,
  AiChatEnhancedMessageBubbleComponent,
  AiChatFileChipComponent,
} from '../messages';
import { StreamDeltaBuffer } from './stream-delta-buffer';

interface PendingMessage {
  id: string;
  text: string;
  at: number;
  attachmentCount: number;
}

/** SD-2326: an in-flight propose-action card awaiting the user's accept/reject. */
interface PendingApprovalVM {
  pendingApprovalId: string;
  payload: DynamicActionCardPayload;
}

/**
 * SD-3016: a message the user has sent that hasn't gone to the server yet. It is shown at once,
 * but goes out only when its files have uploaded, since the message carries their document ids.
 * Sent in order, so a later message never overtakes one still waiting on its files.
 */
interface QueuedMessage {
  /** The optimistic bubble's id. */
  messageId: string;
  text: string;
  /** Files attached from the composer. One whose upload fails is dropped from the message. */
  keys: string[];
  /** Document ids known up front (a retried prompt). */
  docIds: string[];
  ts: number;
  /** The chat it was typed in, resolved when sent — so a chat switch can't redirect it. */
  chatId: Promise<string>;
}

const PENDING_TIMESTAMP_TOLERANCE = 5000;
const MAX_ATTACHMENTS = 8;
const CHAT_CREATE_TIMEOUT_MS = 30_000;
const ATTACHMENT_UPLOAD_ERROR_MESSAGE =
  'Could not attach that file. Please try again.';

@Component({
  selector: 'qt-ai-chat',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TranslationCoreModule,
    AiChatApprovalCardComponent,
    AiChatEnhancedMessageBubbleComponent,
    AiChatFileChipComponent,
  ],
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Accept attribute for the file input (allowed document + image types). */
  readonly allowedAttachmentAccept = ALLOWED_DOCUMENT_ATTACHMENT_ACCEPT;

  /** Inline error when user selects a disallowed file format (shown above input). */
  readonly attachmentFormatError = signal<string | null>(null);

  // Signal-based inputs
  readonly windowId = input.required<string>();
  readonly chatId = input<string | null>();
  /**
   * SD-2716: text to seed the composer with (e.g. an "Ask Allo" button on a survey chart).
   * The draft is PREFILLED only — nothing is sent until the user hits send.
   */
  readonly initialPrompt = input<string>('');
  /** Emitted the first time a non-empty initialPrompt is observed, applied or not. */
  readonly initialPromptConsumed = output<void>();
  /**
   * SD-3045: the chat this component created on its first prompt. `createdChatIdByMysid` cannot
   * tell that apart: the BE sends every new chat to all of the user's `chats_` streams.
   */
  readonly chatCreated = output<string>();
  readonly lazySubscribe = input(false);
  readonly typingLottieSrc = input<string | undefined>(
    'assets/animations/allo.lottie'
  );
  readonly typingLottieSize = input(48);

  @ViewChild('scroll', { static: true }) scroll!: ElementRef<HTMLElement>;
  @ViewChild('inputArea', { static: false })
  inputArea?: ElementRef<HTMLElement>;
  @ViewChild('filePicker', { static: false })
  filePicker?: ElementRef<HTMLInputElement>;
  @ViewChild('chatInput', { static: false })
  chatInput?: ElementRef<HTMLTextAreaElement>;

  /**
   * SD-3016: every attached file by key, with the step it is on. The composer chips and the
   * pills on a message that is still on its way both read from here.
   */
  readonly files = signal<Record<string, ChatFileVM>>({});
  /** Keys of the files in the composer, which go out with the next message. */
  readonly composerKeys = signal<string[]>([]);
  readonly composerFiles = computed(() => {
    const files = this.files();
    return this.composerKeys()
      .map(key => files[key])
      .filter((file): file is ChatFileVM => !!file);
  });

  // Signals for reactive state management
  readonly draft = signal('');
  private lastAppliedSeed = '';
  readonly isTyping = signal(false);
  readonly messages = signal<ChatMsgVM[]>([]);
  readonly pendingMessages = signal<ChatMsgVM[]>([]);
  readonly currentChatId = signal<string | null>(null);
  readonly isDropActive = signal(false);

  readonly toolStatusLabel = signal<string | null>(null);

  /** SD-2326: dynamic approval cards waiting on the user's decision. */
  readonly pendingApprovals = signal<PendingApprovalVM[]>([]);

  /**
   * SD-2155: in-flight bubble assembled from `chatStream` deltas while Allo
   * is generating. Keyed by `chatId+ts`; cleared once the final {@link AiChatEvent}
   * lands with a matching ts (see remoteMessages effect). `null` when no stream
   * is active for the current chat.
   */
  readonly streamingBubble = signal<ChatMsgVM | null>(null);

  /**
   * SD-2526 (Stage A): a BE liveness ping (`keepAlive`) arrived while a turn was
   * in flight and quiet — show a real "still working…" status (BE-confirmed, unlike
   * the always-animating typing lottie). Set by the keepAlive branch of
   * {@link handleChatStream}, cleared by the next real stream event.
   */
  readonly stillWorking = signal(false);

  /**
   * SD-2526 Stage B: assembles the streaming bubble text from seq-keyed deltas so a reconnect
   * replay (out-of-order + duplicate frames) converges with the live stream. Reset per turn /
   * on chat switch. See {@link StreamDeltaBuffer}.
   */
  private readonly streamDeltas = new StreamDeltaBuffer();

  /**
   * SD-2526 Stage B: `ts` of the last turn reconciled to its persisted `chatEvent`. Late stream
   * frames for this ts (e.g. a reconnect replay arriving after `getChatEvents` already reconciled)
   * are ignored, so a completed+committed turn can't be resurrected as a duplicate streaming bubble.
   */
  private reconciledTs: number | null = null;

  /**
   * SD-2716: a `chatStatus` frame arrived after text had already streamed this turn — the
   * agent ran a tool (or paused for approval) and the next LLM round will rewrite the answer
   * from scratch (the BE streams every round down one stream but persists only the last —
   * `AlloGraphBuilder.lastAssistantText`). Armed here, consumed by the NEXT delta, so the
   * bubble is cleared only when replacement text actually starts arriving — no blank gap
   * while the tool runs.
   */
  private pendingRoundBoundary = false;

  // Computed signals
  readonly allMessages = computed(() => {
    const remote = this.remoteMessages();
    const pending = this.pendingMessages();
    const stream = this.streamingBubble();
    // Order matters during a live stream:
    //
    //   remote  → already-committed history (older user/bot turns)
    //   pending → the user message that triggered THIS turn — still
    //             optimistic-only because the BE batches the user-msg
    //             insert + bot-msg insert into a single Kafka transaction
    //             that doesn't broadcast until the entire AI response
    //             completes. So during streaming, `pending` is the only
    //             place the just-sent user message lives.
    //   stream  → the bot bubble being assembled from chatStream deltas,
    //             i.e. the reply TO whatever's in `pending`. Must render
    //             AFTER it.
    //
    // Once the BE transaction commits, the user msg moves pending→remote,
    // the bot msg also lands in remote, and reconcileStreamingBubble drops
    // the stream entry. Final order resolves to plain [...remote] cleanly.
    return stream ? [...remote, ...pending, stream] : [...remote, ...pending];
  });

  /**
   * SD-3016: each user message's files with their progress, by message id. A message still on its
   * way knows its files by key. Once the server echoes it back (right after it is sent, while the
   * files are still being scanned), the echo is matched by document id instead. Files from before
   * a reload aren't known here, so those messages keep their plain pills.
   */
  readonly messageFiles = computed(() => {
    const files = this.files();
    const known = Object.values(files);
    const byMessage: Record<string, ChatFileVM[]> = {};
    // Not allMessages(): it includes the streaming bubble, which changes on every delta.
    for (const message of [
      ...this.remoteMessages(),
      ...this.pendingMessages(),
    ]) {
      if (message.author !== 'user') continue;
      const ids = message.documentIds ?? [];
      const found = message.fileKeys?.length
        ? message.fileKeys.map(key => files[key])
        : known.filter(f => f.documentId && ids.includes(f.documentId));
      const present = found.filter((f): f is ChatFileVM => !!f);
      if (present.length) byMessage[message.id] = present;
    }
    return byMessage;
  });

  /**
   * SD-3016: a sent message is waiting for its files to upload. Sending is held meanwhile, as it
   * is while Allo answers: a second message would reach the server first and be answered without
   * the files.
   */
  readonly waitingToSend = signal(false);

  /**
   * Drive the typing lottie off the actual in-flight state so it stays visible
   * for the WHOLE turn — the pre-token "Agents thinking..." phase ({@link isTyping})
   * AND while the response is streaming ({@link streamingBubble} `streaming`).
   * This keeps the animation consistent across the first and every subsequent
   * response, instead of it vanishing on the first delta.
   *
   * SD-2450: still gated off while an interrupted/errored bubble is showing —
   * the turn is over (the bubble carries the Retry affordance), so the lottie
   * must not linger even if the typing-state reconcile briefly flips
   * {@link isTyping} back on.
   */
  readonly showTyping = computed(() => {
    const stream = this.streamingBubble();
    if (stream?.errored) return false;
    // SD-2674: once the streaming bubble exists it renders the thinking dots
    // itself, as a tail beneath the answer it is accumulating. A standalone
    // typing bubble on top of that would show a second, duplicate set.
    if (stream?.streaming) return false;
    return this.isTyping();
  });

  /**
   * SD-2674: no history and nothing in flight — the scroll area shows the
   * centered welcome state instead of messages.
   */
  readonly isEmptyChat = computed(
    () => this.allMessages().length === 0 && !this.isTyping()
  );

  /**
   * SD-2765: a turn is in flight, so the send arrow becomes a stop control.
   *
   * Covers the whole turn: the pre-token "thinking" phase ({@link isTyping}), where there is
   * nothing to see yet but the server is very much working, and the streaming phase. Not
   * {@link showTyping}: since SD-2674 that goes false once the streaming bubble exists (the bubble
   * draws its own dots), which made the stop control vanish at the first delta. Goes false on an
   * errored/stopped bubble and on `complete`, so a finished turn does not leave a stop button behind.
   */
  readonly isResponding = computed(() => {
    const stream = this.streamingBubble();
    if (stream?.errored) return false;
    if (stream?.streaming) return true;
    return this.isTyping();
  });

  /**
   * SD-2765: ask the server to stop the current turn.
   *
   * Fire-and-forget: the BE replies on the turn's own thread with the terminal `cancelled` stream
   * signal, which {@link handleChatStream} turns into the stopped bubble. Nothing is changed
   * optimistically here — if the turn finished a moment before the click, the server ignores the
   * stop and the normal completion still lands.
   */
  stop(): void {
    const chatId = this.currentChatId();
    if (!chatId) return;
    this.chatSvc.cancelStream(chatId);
  }

  readonly canAttach = computed(
    () => this.composerKeys().length < MAX_ATTACHMENTS
  );

  /**
   * SD-2326: an approval card is open. Only one propose_action interrupt can be
   * paused per chat (single LangGraph checkpoint), so a new message would just
   * be rejected by the BE — block sending and surface a hint until it's resolved.
   */
  readonly hasPendingApproval = computed(
    () => this.pendingApprovals().length > 0
  );

  /** SD-3016: uploads no longer block sending; the message waits for its files instead. */
  readonly canSend = computed(
    () =>
      this.draft().trim().length > 0 &&
      !this.hasPendingApproval() &&
      !this.waitingToSend()
  );

  readonly sendHint = computed(() =>
    this.waitingToSend()
      ? 'Allo is waiting for your files'
      : 'Type a message to send'
  );

  getPreviousMessageText(index: number): string {
    const list = this.allMessages();
    const prev = list[index - 1];
    return prev?.author === 'user' ? (prev.text ?? '') : '';
  }

  readonly typingDots = [0, 1, 2];
  readonly typingMessage: ChatMsgVM = {
    id: 'typing',
    text: '',
    author: 'bot',
    at: Date.now(),
  };

  // Private state
  /** Per file: its upload, then the watch on its scan and text reading. */
  private readonly fileSubs = new Map<string, Subscription>();
  /** The picked files, kept until uploaded so "Try again" can resend them. */
  private readonly uploadSources = new Map<string, File>();
  private outbox: QueuedMessage[] = [];
  private uploadSeq = 0;
  private chatIdPromise: Promise<string> | null = null;
  private attachmentNamesByMessageKey = new Map<string, string[]>();
  private destroy$ = new Subject<void>();
  private chatId$ = new BehaviorSubject<string | null>(null);
  private typingSubject = new BehaviorSubject<boolean>(false);
  private lastPendingUserTs: number | null = null;
  private pendingMsgsSubject = new BehaviorSubject<ChatMsgVM[]>([]);
  private pendingQueue: PendingMessage[] = [];
  private hasSubscribedToChats = false;
  private dragCounter = 0;
  private activeChatSubscriptionId: string | null = null;
  private focusObserver: IntersectionObserver | null = null;
  private wasInputIntersecting = false;

  // Signal-based state
  private readonly resolvedChatId = signal<string | null>(null);
  private readonly remoteMessages = signal<ChatMsgVM[]>([]);

  // Signal-based injections
  private readonly aiChatStore = inject(AiChatStore);
  private readonly createdChatId = computed(
    () =>
      this.aiChatStore.createdChatIdByMysid()[`chats_${this.windowId()}`] ??
      null
  );
  private readonly chatDocStore = inject(ChatDocumentReferenceStore);
  private readonly chatDocService = inject(ChatDocumentReferenceService);
  private readonly scanGate = inject(DocumentScanGate);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly chatSvc = inject(AiChatService);
  private readonly wsErrorSuppression = inject(WsErrorSuppressionService);

  constructor() {
    // Effect to sync typing state
    effect(() => {
      const typing = this.isTyping();
      this.typingSubject.next(typing);
    });

    // SD-2716: seed the composer from an "Ask Allo" entry point. Prefill only — we never
    // send on the user's behalf — and never clobber something they have already typed;
    // an unsent earlier seed is not theirs, so the next seed replaces it.
    // The seed is reported spent as soon as it is read, so a skipped seed is not re-applied
    // to the next component that renders with it.
    effect(() => {
      const prompt = this.initialPrompt();
      if (!prompt) return;
      untracked(() => {
        this.initialPromptConsumed.emit();
        const draft = this.draft();
        if (draft.trim().length > 0 && draft !== this.lastAppliedSeed) return;
        this.lastAppliedSeed = prompt;
        this.draft.set(prompt);
        this.focusChatInput();
      });
    });

    // Effect to sync messages
    effect(() => {
      this.allMessages();
      untracked(() => this.scrollToBottomSoon());
    });

    // Effect to resolve chat ID from multiple sources
    effect(() => {
      const resolvedId = this.chatId() || this.createdChatId() || null;

      untracked(() => {
        if (this.chatId$.value !== resolvedId) {
          this.clearAllPending();
        }
        this.chatId$.next(resolvedId);
        this.currentChatId.set(resolvedId);
        this.resolvedChatId.set(resolvedId);
      });
    });

    // Effect to handle remote messages based on resolved chat ID
    effect(() => {
      const chatId = this.resolvedChatId();

      if (!chatId) {
        this.releaseChatEventSubscription();
        this.remoteMessages.set([]);
        return;
      }

      const events = this.aiChatStore.eventsFor(chatId)();

      untracked(() => {
        // Subscribe to chat events for this chat ID (ref-counted)
        this.ensureChatEventSubscription(chatId);

        const messages = events.map(event => this.toChatMessage(event));
        const enhanced = messages.map(msg => ({ ...msg }));
        this.reconcilePending(enhanced);
        this.remoteMessages.set(enhanced);
        // SD-2155: drop the in-flight streaming bubble once the canonical
        // chatEvent with matching ts lands — that's the source of truth
        // now, no point double-rendering.
        this.reconcileStreamingBubble(enhanced);
        this.cdr.markForCheck();

        if (!enhanced.length) {
          if (!this.pendingQueue.length) {
            this.isTyping.set(false);
            this.toolStatusLabel.set(null);
            this.lastPendingUserTs = null;
          } else {
            this.isTyping.set(true);
          }
          return;
        }

        if (this.lastPendingUserTs !== null) {
          for (let i = enhanced.length - 1; i >= 0; i--) {
            const candidate = enhanced[i];
            if (candidate.author === 'bot') {
              if (candidate.at >= this.lastPendingUserTs) {
                this.isTyping.set(false);
                this.toolStatusLabel.set(null);
                this.lastPendingUserTs = null;
              }
              break;
            }
          }
        }
      });
    });

    this.chatSvc.chatStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status: ChatStatusPayload) => {
        const activeChatId = this.currentChatId();
        if (!activeChatId || status.chatId !== activeChatId) return;

        // SD-2716: a tool status (or an approval interrupt) mid-turn means a new LLM round
        // is coming and it will rewrite the answer. Arm only once this turn has actually
        // streamed text — a tool called before the first token has nothing to supersede.
        const inFlight = this.streamingBubble();
        if (inFlight?.streaming && inFlight.text.length > 0) {
          this.pendingRoundBoundary = true;
        }

        if (isPendingApprovalStatus(status)) {
          this.handlePendingApproval(status);
          return;
        }

        if (isActionCardStatus(status)) {
          this.handleActionCard(status);
          return;
        }

        this.toolStatusLabel.set(status.label);
        this.cdr.markForCheck();
      });

    // SD-2155: assemble the in-flight assistant bubble from BE token deltas.
    // The bubble sits in allMessages between remote and pending, gets text
    // appended on each delta, and reconciles against the final chatEvent
    // (same ts) when it arrives via remoteMessages effect.
    this.chatSvc.chatStream$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: ChatStreamPayload) => {
        const activeChatId = this.currentChatId();
        if (!activeChatId || payload.chatId !== activeChatId) return;
        this.handleChatStream(payload);
      });

    // SD-2526 Stage B: on websocket reconnect, recover an in-flight stream. init$ also fires
    // on the first connect, so we gate on an active (non-errored) bubble to avoid a no-op storm.
    this.chatSvc.reconnected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onWebSocketReconnected());
  }

  /**
   * SD-2155: per-event handler for the chatStream WS subscription. Branches
   * on `type`:
   *
   * - `start`    — create a fresh streaming bubble keyed by chatId+ts. Don't
   *                clear the typing indicator yet; we're still waiting for
   *                first content.
   * - `delta`    — append `delta` text to the streaming bubble. First delta
   *                also flips off the typing indicator and tool-status label
   *                because we're now visibly rendering, not "thinking".
   * - `complete` — mark the bubble as no-longer-streaming; final chatEvent
   *                will arrive shortly and replace it via reconcile.
   */
  private handleChatStream(payload: ChatStreamPayload): void {
    const { chatId, ts, type, delta } = payload;
    // SD-2526 Stage B: ignore any late frame for a turn already reconciled to its persisted
    // chatEvent (a reconnect replay can arrive after the parallel getChatEvents reconciled the
    // bubble) — otherwise the delta branch would bootstrap a duplicate bubble for a committed turn.
    if (ts === this.reconciledTs) return;
    // SD-2526: any real stream event (content or terminal) clears the transient
    // "still working…" liveness hint; only a keepAlive (below) sets it.
    if (type !== 'keepAlive') this.stillWorking.set(false);

    if (type === 'resyncMiss') {
      // SD-2526 Stage B: the BE couldn't replay (turn evicted/unknown). No-op here — the
      // parallel getChatEvents reload fired on reconnect reconciles a completed turn.
      return;
    }

    if (type === 'start') {
      this.streamDeltas.reset();
      this.pendingRoundBoundary = false;
      this.reconciledTs = null; // fresh turn — this ts hasn't reconciled
      const bubble: ChatMsgVM = {
        id: `stream_${chatId}_${ts}`,
        chatId,
        ts,
        at: ts,
        text: '',
        author: 'bot',
        streaming: true,
      };
      this.streamingBubble.set(bubble);
      this.cdr.markForCheck();
      return;
    }

    if (type === 'delta') {
      const seq = payload.seq;
      const current = this.streamingBubble();
      // SD-2526 Stage B: ignore a delta for a turn other than the one currently rendering — e.g. a
      // late replay for an older, already-superseded turn (its resync reply landing after a new turn
      // started) must not corrupt the active bubble. Mirrors the `complete` branch's ts guard.
      if (current && current.ts != null && current.ts !== ts) return;
      // SD-2526 Stage B: a duplicate (already-applied) seq — e.g. a replayed frame whose live
      // twin we already have — is an idempotent no-op (skip the redundant re-render).
      if (
        seq != null &&
        current &&
        seq <= this.streamDeltas.highestContiguousSeq
      ) {
        return;
      }
      // Defensive: if `start` was missed (page reloaded mid-stream, etc.),
      // bootstrap the bubble lazily off the first delta we see.
      let base: ChatMsgVM = current ?? {
        id: `stream_${chatId}_${ts}`,
        chatId,
        ts,
        at: ts,
        text: '',
        author: 'bot',
        streaming: true,
      };
      // SD-2716: first delta of a new agent round — REPLACE, don't append. The BE streams
      // every round of the tool loop down one stream but persists only the last round's
      // text, so appending made one turn look like the same answer re-streaming N times
      // before collapsing to the shorter persisted one. Consumed here, after the duplicate
      // guard, so a replayed frame can't burn the boundary; and here rather than on the
      // status frame itself so the bubble keeps the previous round's text while the tool
      // runs.
      if (this.pendingRoundBoundary) {
        this.pendingRoundBoundary = false;
        this.streamDeltas.startRound();
        base = { ...base, text: '' };
      }
      // With a seq, render the contiguous prefix from the seq-indexed buffer (replay-safe);
      // without one, fall back to a plain append.
      const text =
        seq != null
          ? this.streamDeltas.apply(seq, delta ?? '')
          : base.text + (delta ?? '');
      this.streamingBubble.set({ ...base, text });
      // First visible token → stop showing "Agents thinking..." / tool spinner.
      if (this.isTyping()) {
        this.isTyping.set(false);
        this.toolStatusLabel.set(null);
      }
      this.cdr.markForCheck();
      return;
    }

    if (type === 'complete') {
      const current = this.streamingBubble();
      if (current && current.ts === ts) {
        // Keep the assembled text visible but flip off the streaming flag so
        // feedback/tools-summary UI shows immediately. The final chatEvent will
        // land shortly and the remoteMessages effect will swap this bubble out.
        this.streamingBubble.set({ ...current, streaming: false });
        // Streaming is done — clear the thinking state too so the typing lottie
        // hides exactly here and can't linger in the window between `complete`
        // and the final chatEvent landing.
        this.isTyping.set(false);
        this.toolStatusLabel.set(null);
        this.pendingRoundBoundary = false;
        this.cdr.markForCheck();
      }
      return;
    }

    if (type === 'error') {
      // SD-2450: the turn failed (mid-stream drop or other). Flip the bubble into
      // an interrupted state — keep whatever partial text arrived but stop the
      // spinner and render the retry affordance. No final chatEvent will land, so
      // the bubble stays put until the user retries (which clears it).
      const current = this.streamingBubble();
      const base: ChatMsgVM = current ?? {
        id: `stream_${chatId}_${ts}`,
        chatId,
        ts,
        at: ts,
        text: '',
        author: 'bot',
      };
      this.streamingBubble.set({
        ...base,
        streaming: false,
        errored: true,
        errorCode: payload.code ?? 'failed',
      });
      this.isTyping.set(false);
      this.toolStatusLabel.set(null);
      this.pendingRoundBoundary = false;
      // The failed turn is now surfaced inline here; suppress the redundant
      // global "Internal error" dialog the rethrown failure would otherwise raise.
      this.wsErrorSuppression.suppressNextChatEventError();
      this.cdr.markForCheck();
      return;
    }

    if (type === 'keepAlive') {
      // SD-2526 Stage A: the BE confirmed the generation is still alive (just slow).
      // Show a real "still working…" status while a turn is in flight; it clears as
      // soon as content resumes or the turn ends (handled at the top of this method).
      if (this.streamingBubble()?.streaming || this.isTyping()) {
        this.stillWorking.set(true);
        this.cdr.markForCheck();
      }
      return;
    }
  }

  /**
   * SD-2526 Stage B: the websocket reconnected. If a stream is still in flight for the open chat,
   * ask the BE to replay the frames we missed (from our last contiguous seq) AND reload the chat's
   * events — the safety net for a turn that COMPLETED during the outage (its persisted chatEvent
   * reconciles the bubble). Replayed frames flow back through the normal chatStream path.
   */
  private onWebSocketReconnected(): void {
    const bubble = this.streamingBubble();
    const chatId = this.currentChatId();
    if (!bubble || bubble.errored || bubble.ts == null || !chatId) return;

    this.aiChatStore.resyncStream({
      chatId,
      ts: bubble.ts,
      lastSeq: this.streamDeltas.highestContiguousSeq,
    });
    this.aiChatStore.getChatEvents({
      mysid: `chatEvent_${chatId}`,
      prms: { chatId },
    });
  }

  /**
   * SD-2450/SD-2765: re-issue the prompt that produced the bubble at `index`, after an
   * interrupted, failed or stopped turn.
   *
   * <p>Retry used to replay a single `lastUserPrompt` field. That was invisible while an
   * interrupted bubble could only ever be the last thing in the chat and was cleared on the next
   * send. A stopped turn persists as a real chatEvent, so its button outlives later messages — and
   * was replaying whatever had been sent most recently instead of its own prompt. After a reload
   * the field is null, so the same button did nothing at all.
   */
  retryFrom(index: number): void {
    const list = this.allMessages();
    const bubble = list[index];
    if (!bubble) return;

    const prompt = findPromptForBubble(list, index);
    if (!prompt) return;

    // Only the live bubble is a wire-only preview worth removing. A persisted stopped turn stays
    // in history and the retry lands below it as a new exchange.
    if (bubble.id === this.streamingBubble()?.id) {
      this.streamingBubble.set(null);
    }
    this.queueMessage(prompt.text, [], [...(prompt.documentIds ?? [])]);
  }

  /**
   * SD-2326: parse the cardJson payload off a `pending_approval` chatStatus
   * event and push it onto the in-flight list so the template renders an
   * approval card below the chat stream. Defensive — bad/empty cardJson is
   * logged and dropped rather than crashing the chat thread.
   */
  private handlePendingApproval(
    status: ChatStatusPendingApprovalPayload
  ): void {
    let card: unknown;
    try {
      card = JSON.parse(status.cardJson);
    } catch (e) {
      console.warn('Failed to parse pending_approval cardJson', e);
      return;
    }
    if (!isDynamicActionCardPayload(card)) {
      console.warn(
        'pending_approval cardJson did not match DynamicActionCardPayload shape',
        card
      );
      return;
    }
    this.pendingApprovals.update(arr => [
      ...arr,
      { pendingApprovalId: status.pendingApprovalId, payload: card },
    ]);
    this.toolStatusLabel.set(null);
    this.isTyping.set(false);
  }

  /** SD-2326: remove a resolved approval card from the in-flight list. */
  protected onApprovalResolved(pendingApprovalId: string): void {
    this.pendingApprovals.update(arr =>
      arr.filter(pa => pa.pendingApprovalId !== pendingApprovalId)
    );
  }

  ngOnInit(): void {
    const chatIdValue = this.chatId();
    if (chatIdValue) this.chatId$.next(chatIdValue);

    if (!this.lazySubscribe()) {
      this.ensureChatSubscription();
    }
  }

  ngAfterViewInit(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) return;
    this.focusObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;
        const isVisible = entry.isIntersecting;
        if (isVisible && !this.wasInputIntersecting) {
          el.focus();
        }
        this.wasInputIntersecting = isVisible;
      },
      { threshold: 0.1 }
    );
    this.focusObserver.observe(el);
  }

  onChatAreaClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, [role="button"], input, textarea')) return;
    this.focusChatInput();
  }

  private focusChatInput(): void {
    this.chatInput?.nativeElement?.focus();
  }

  ngOnDestroy(): void {
    this.focusObserver?.disconnect();
    this.focusObserver = null;
    this.aiChatStore.unsubscribeChats(`chats_${this.windowId()}`);
    this.releaseChatEventSubscription();

    this.destroy$.next();
    this.destroy$.complete();
    this.typingSubject.complete();
    this.pendingMsgsSubject.complete();
    this.fileSubs.clear();
  }

  openFilePicker(): void {
    this.filePicker?.nativeElement?.click();
  }

  private readonly chatInputMinHeight = 32;

  growChatInput(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) return;
    const isEmpty = !el.value.trim();
    if (isEmpty) {
      el.style.height = `${this.chatInputMinHeight}px`;
      return;
    }
    el.style.height = '0';
    const newHeight = Math.max(
      this.chatInputMinHeight,
      Math.min(el.scrollHeight, 200)
    );
    el.style.height = `${newHeight}px`;
  }

  onChatKeydown(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.key !== 'Enter') return;
    if (e.shiftKey) return;
    e.preventDefault();
    this.send();
  }

  /**
   * Removes a composer chip, aborting its upload if it's still running (a cancelled upload must
   * not land and attach behind the user's back).
   *
   * SD-2631: v2 upload commits the reference server-side, so an uploaded file is already attached
   * to the chat. Detaching only in the UI would leave a user-declined attachment silently readable
   * by the chat/agent — so this also deletes the row, not just the chip.
   */
  removeComposerFile(key: string): void {
    const referenceId = this.files()[key]?.referenceId;
    this.composerKeys.update(keys => keys.filter(k => k !== key));
    this.forgetFile(key);
    if (referenceId) this.discardReference(referenceId);
  }

  /** SD-3016: "Try again" on a composer file whose upload failed. */
  retryUpload(key: string): void {
    if (this.files()[key]?.failure !== 'upload' || !this.uploadSources.has(key))
      return;
    this.patchFile(key, { step: 'uploading', progress: 0, failure: undefined });
    this.ensureChatId().then(
      chatId => this.startUpload(key, chatId),
      error => {
        console.error('Could not create a chat for the attachment', error);
        this.failUpload(key);
      }
    );
  }

  /** Best-effort — the chip is gone from the UI either way. */
  private discardReference(referenceId: string): void {
    this.chatDocStore
      .remove(referenceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: e => console.error('Failed to delete chat attachment', e),
      });
  }

  onFilesSelected(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    const files = input?.files ? Array.from(input.files) : [];
    this.uploadFiles(files, () => {
      if (input) input.value = '';
    });
  }

  onDragEnter(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDropActive.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const container = this.inputArea?.nativeElement;
    const related = event.relatedTarget as Node | null;
    const stillInside = container && related && container.contains(related);
    if (!stillInside) {
      this.dragCounter = Math.max(0, this.dragCounter - 1);
      if (this.dragCounter === 0) {
        this.isDropActive.set(false);
      }
    }
  }

  onDrop(event: DragEvent): void {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.dragCounter = 0;
    this.isDropActive.set(false);
    setTimeout(() => {
      this.uploadFiles(files);
    }, 0);
  }

  /**
   * Ensure we have a chatId — signal-based approach. Single-flight: v2
   * attachment uploads need the chatId up front (see `uploadFiles`), so this
   * now races `queueMessage` for chat creation. Without the latch, two
   * concurrent callers would each dispatch their own `createChat` and end up
   * with the files on one chat and the message on another.
   */
  private ensureChatId(): Promise<string> {
    const currentId = this.chatId$.value;
    if (currentId) return Promise.resolve(currentId);
    if (this.chatIdPromise) return this.chatIdPromise;

    this.ensureChatSubscription();
    const mysid = `chats_${this.windowId()}`;
    const payload: AiChatInsertPayload = {
      res: {},
      s: 'model',
      view: 'pln',
    };

    const promise = new Promise<string>((resolve, reject) => {
      this.aiChatStore.chatCreated$
        .pipe(
          filter(created => created.mysid === mysid),
          map(created => created.chatId),
          take(1),
          timeout(CHAT_CREATE_TIMEOUT_MS),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: newId => {
            this.chatId$.next(newId);
            this.chatCreated.emit(newId);
            resolve(newId);
          },
          error: reject,
        });
    });
    this.aiChatStore.createChat(payload);

    this.chatIdPromise = promise;
    const clear = () => {
      if (this.chatIdPromise === promise) this.chatIdPromise = null;
    };
    promise.then(clear, clear);
    return promise;
  }

  /**
   * SD-3016: shows the message at once and queues it until its files have uploaded. The caller
   * clears the composer first, so anything typed next is a new message rather than lost.
   */
  private queueMessage(text: string, keys: string[], docIds: string[]): void {
    const files = this.files();
    const ts = Date.now();
    const names = keys
      .map(key => files[key]?.name)
      .filter((name): name is string => !!name);
    const messageId = this.addPendingMessage(
      text,
      ts,
      keys.length + docIds.length,
      names,
      docIds,
      keys
    );
    const chatId = this.ensureChatId();
    // Handled in flushOutbox; this only keeps a failure there from also being reported as unhandled.
    chatId.catch((): void => undefined);
    this.outbox.push({ messageId, text, keys, docIds, ts, chatId });
    this.flushOutbox();
    this.scrollToBottomSoon();
  }

  /** Sends queued messages in order, each once none of its files is still uploading. */
  private flushOutbox(): void {
    while (this.outbox.length) {
      const next = this.outbox[0];
      const files = this.files();
      if (next.keys.some(key => files[key]?.step === 'uploading')) break;
      this.outbox.shift();

      const uploaded = next.keys.filter(key => files[key]?.documentId);
      const docIds = [
        ...next.docIds,
        ...uploaded.map(key => files[key].documentId as string),
      ];
      if (!next.text && !docIds.length) {
        // No text, and every file failed to upload: nothing is left to send.
        this.removePendingMessage(next.messageId);
        continue;
      }
      const names = uploaded.map(key => files[key].name);
      next.chatId.then(
        chatId => this.dispatch(chatId, next, docIds, names),
        error => {
          console.error('Could not create a chat for the message', error);
          this.removePendingMessage(next.messageId);
          // Give the text back rather than losing it, unless the user is already typing another.
          if (!this.draft().trim()) this.draft.set(next.text);
        }
      );
    }
    this.waitingToSend.set(this.outbox.length > 0);
  }

  /** Sends the chatEvent insert for a queued message. */
  private dispatch(
    chatId: string,
    message: QueuedMessage,
    docIds: string[],
    names: string[]
  ): void {
    // SD-2537: the v2 BE (ChatEventApi) expects { chat, data } — `src` and the event ts are
    // assigned server-side. `ts` is kept only for the local optimistic bubble.
    // `documents` carries DOCUMENT ids (ChatEvent's ManyToMany join is to
    // Document via chat_event_document), not reference ids. Only sent when
    // non-empty, so a text-only prompt stays byte-identical to before.
    const res: Record<string, unknown> = { chat: chatId, data: message.text };
    if (docIds.length) res['documents'] = docIds;
    const payload: AiChatInsertPayload = {
      id: chatId,
      res,
      s: 'model',
      view: 'pln',
    };
    this.aiChatStore.sendMessage(payload);

    // Sent from a chat the user has since left: nothing on screen belongs to it.
    if (chatId !== this.chatId$.value) return;

    this.lastPendingUserTs = message.ts;
    this.isTyping.set(true);
    // No generic filler label — the typing dots alone carry the pre-token
    // state (survey-Allo pattern); the label appears only for real tool
    // activity pushed over chatStatus ("Reading the deal schema…").
    this.toolStatusLabel.set(null);
    if (names.length) {
      this.attachmentNamesByMessageKey.set(`${chatId}_${message.ts}`, names);
    }
    // Retry replays a prompt from its documentIds, which are only known now.
    this.pendingMessages.update(list =>
      list.map(m =>
        m.id === message.messageId ? { ...m, documentIds: docIds } : m
      )
    );
    this.cdr.markForCheck();
  }

  // send handler - signal-based approach
  /**
   * SD-2674: starter prompts offered on the empty state. One is phrased to
   * elicit an inline chart so a first-time user sees that capability.
   */
  readonly starterPrompts: readonly string[] = [
    'Summarize my active deals',
    'Chart my pipeline by deal stage',
    'What changed across my deals this week?',
    'Draft a note summarizing my latest deal',
  ];

  /** Sends a starter-prompt chip through the normal send path. */
  sendPrompt(prompt: string): void {
    if (this.hasPendingApproval()) return;
    this.draft.set(prompt);
    this.send();
  }

  /**
   * SD-3016: sends even while files upload. The message shows at once with its files, the
   * composer clears and stays usable, and the message goes out once its files have uploaded.
   */
  send(): void {
    // Enter while Allo answers waits like the (hidden) Send button: sent mid-turn, the message is
    // answered on its own before the current turn ends, without the files that turn is reading.
    if (
      this.hasPendingApproval() ||
      this.waitingToSend() ||
      this.isResponding()
    )
      return;
    const text = this.draft()?.trim() ?? '';
    // A failed upload stays in the composer with its "Try again"; the rest go with this message.
    // A file the scan blocked goes too, so Allo can say which file it couldn't read and why.
    const files = this.files();
    const keys = this.composerKeys().filter(
      key => files[key]?.failure !== 'upload'
    );
    if (!text && !keys.length) return;

    this.attachmentFormatError.set(null);
    this.composerKeys.update(list => list.filter(key => !keys.includes(key)));
    this.draft.set('');
    const inputEl = this.chatInput?.nativeElement;
    if (inputEl) inputEl.style.height = `${this.chatInputMinHeight}px`;
    this.queueMessage(text, keys, []);
  }

  private scrollToBottomSoon() {
    queueMicrotask(() => {
      // SD-2674: on an empty chat the scroll area holds the vertically-centered
      // welcome state. Pinning it to the bottom would push the avatar and
      // heading up under the panel header, so leave the scroll position alone.
      if (this.isEmptyChat()) return;

      const el = this.scroll?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  private ensureChatSubscription(): void {
    if (this.hasSubscribedToChats) return;

    const mysid = `chats_${this.windowId()}`;
    const payload: AiChatSubscribePayload = {
      mysid,
    };
    this.aiChatStore.subscribeChats(payload);
    this.hasSubscribedToChats = true;
  }

  private toChatMessage(event: AiChatEvent): ChatMsgVM {
    const at = event.ts ?? Date.now();
    const author: Author = event.src === 'user' ? 'user' : 'bot';
    const text = event.data ?? '';
    const attachmentCount = event.documentIds?.length ?? 0;

    const msg: ChatMsgVM = {
      id: `${event.chatId}_${at}`,
      text,
      author,
      at,
      attachmentCount,
      chatId: event.chatId,
      ts: event.ts,
    };

    // SD-2765: the terminal stream signal only reached whoever was watching at the time, so a
    // reloaded chat relies on this flag to render the partial as stopped rather than as a reply
    // that happens to end mid-sentence. Same bubble treatment as an interrupted turn, different copy.
    if (event.cancelled) {
      msg.errored = true;
      msg.errorCode = 'cancelled';
    }

    if (author === 'user' && attachmentCount > 0) {
      msg.documentIds = [...(event.documentIds ?? [])];
      const cached = this.attachmentNamesByMessageKey.get(
        `${event.chatId}_${at}`
      );
      if (cached?.length) {
        msg.attachmentNames = cached;
      }
    }

    if (event.toolsUsed?.length) {
      msg.toolsUsed = event.toolsUsed;
    }

    // SD-2865: cards the BE captured when each tool returned (SD-2798). Events
    // written before that carry their cards inside `data` instead, and the
    // bubble still parses those.
    if (event.actionCards?.length) {
      msg.actionCards = event.actionCards;
    }

    return msg;
  }

  /**
   * SD-2865: a card a tool just returned (SD-2798). It arrives the moment the tool
   * completes, before the model has written any of its reply, so it is attached to
   * the in-flight bubble and renders straight away. The same card comes back on the
   * persisted event, which replaces this bubble when the turn completes.
   *
   * <p>No bubble yet means the card arrived between turns — a stale frame for a turn
   * already reconciled. Dropping it is right: the persisted event carries the card.
   */
  private handleActionCard(status: ChatStatusActionCardPayload): void {
    const bubble = this.streamingBubble();
    if (!bubble) return;

    const cards = appendStreamedActionCard(bubble.actionCards, status.cardJson);
    if (!cards) return;

    this.streamingBubble.set({ ...bubble, actionCards: cards });
    this.cdr.markForCheck();
  }

  private ensureChatEventSubscription(chatId: string): void {
    if (this.activeChatSubscriptionId === chatId) return;

    this.releaseChatEventSubscription();

    this.aiChatStore.acquireEvents(chatId);
    this.activeChatSubscriptionId = chatId;
  }

  private releaseChatEventSubscription(): void {
    if (!this.activeChatSubscriptionId) return;
    this.aiChatStore.releaseEvents(this.activeChatSubscriptionId);
    this.activeChatSubscriptionId = null;
  }

  private addPendingMessage(
    text: string,
    at: number,
    attachmentCount: number,
    attachmentNames: string[],
    documentIds: string[],
    fileKeys: string[]
  ): string {
    const id = `pending_${at}_${Math.random().toString(16).slice(2)}`;
    const message: ChatMsgVM = {
      id,
      text,
      author: 'user',
      at,
      attachmentCount,
      attachmentNames,
      documentIds: [...documentIds],
      fileKeys,
    };

    this.pendingQueue.push({
      id,
      text: text.trim(),
      at,
      attachmentCount,
    });
    this.pendingMessages.update(messages => [...messages, message]);
    this.cdr.markForCheck();
    return id;
  }

  private removePendingMessage(id: string): void {
    this.pendingQueue = this.pendingQueue.filter(p => p.id !== id);
    this.pendingMessages.update(list => list.filter(m => m.id !== id));
    this.cdr.markForCheck();
  }

  /**
   * SD-3016: takes a file whose upload failed off a message still on its way. False when that
   * message is no longer on screen (the user switched chats).
   */
  private dropFileFromMessage(messageId: string, key: string): boolean {
    if (!this.pendingMessages().some(m => m.id === messageId)) return false;
    const name = this.files()[key]?.name;
    this.pendingMessages.update(list =>
      list.map(m => {
        if (m.id !== messageId) return m;
        const names = [...(m.attachmentNames ?? [])];
        const index = name ? names.indexOf(name) : -1;
        if (index !== -1) names.splice(index, 1);
        return {
          ...m,
          fileKeys: (m.fileKeys ?? []).filter(k => k !== key),
          attachmentNames: names,
          attachmentCount: Math.max(0, (m.attachmentCount ?? 0) - 1),
        };
      })
    );
    const queued = this.pendingQueue.find(p => p.id === messageId);
    if (queued)
      queued.attachmentCount = Math.max(0, queued.attachmentCount - 1);
    return true;
  }

  /**
   * SD-2155: clear the in-flight streaming bubble once the canonical
   * chatEvent with matching ts lands. The remote message is the source of
   * truth (it's been through the BE output guardrail + DDB persistence) and
   * the streaming bubble was always a wire-only preview.
   */
  private reconcileStreamingBubble(remote: ChatMsgVM[]): void {
    const stream = this.streamingBubble();
    if (!stream || stream.ts == null) return;
    const matched = remote.some(
      msg => msg.author === 'bot' && msg.ts === stream.ts
    );
    if (matched) {
      this.streamingBubble.set(null);
      // SD-2526 Stage B: this turn is now committed — remember its ts and drop the seq store so a
      // late reconnect replay for it is ignored (see the guard in handleChatStream) instead of
      // resurrecting a duplicate bubble.
      this.reconciledTs = stream.ts;
      this.streamDeltas.reset();
      this.pendingRoundBoundary = false;
      // Clear the "still working…" liveness banner: a turn that emitted a keepAlive and then
      // completed during an outage reconciles here via getChatEvents, and the late complete for
      // its ts is short-circuited by the reconciledTs guard in handleChatStream — so this is the
      // one place left to drop the banner, else it strands next to the finished answer.
      this.stillWorking.set(false);
    }
  }

  private reconcilePending(remote: ChatMsgVM[]): void {
    if (!this.pendingQueue.length) return;

    const remoteUser = remote.filter(msg => msg.author === 'user');
    if (!remoteUser.length) return;

    let pendingList = this.pendingMessages();
    let changed = false;

    this.pendingQueue = this.pendingQueue.filter(pending => {
      const match = remoteUser.find(msg => {
        const messageAt = msg.at ?? 0;
        const timeDiff = Math.abs(messageAt - pending.at);
        const withinWindow =
          messageAt >= pending.at || timeDiff <= PENDING_TIMESTAMP_TOLERANCE;
        return withinWindow && msg.text.trim() === pending.text.trim();
      });

      if (match) {
        if (
          pending.attachmentCount > 0 &&
          (!match.attachmentCount ||
            match.attachmentCount < pending.attachmentCount)
        ) {
          match.attachmentCount = pending.attachmentCount;
        }
        const idx = pendingList.findIndex(m => m.id === pending.id);
        if (idx !== -1) {
          pendingList = [
            ...pendingList.slice(0, idx),
            ...pendingList.slice(idx + 1),
          ];
          changed = true;
        }
        return false;
      }

      return true;
    });

    if (changed) {
      this.pendingMessages.set(pendingList);
      this.cdr.markForCheck();
    }
  }

  private uploadFiles(files: File[], done?: () => void): void {
    if (!files?.length) {
      done?.();
      return;
    }

    const allowed = files.filter(isAllowedDocumentAttachment);
    const rejectedCount = files.length - allowed.length;
    this.attachmentFormatError.set(
      rejectedCount > 0
        ? ALLOWED_DOCUMENT_ATTACHMENT_FORMAT_ERROR_MESSAGE
        : null
    );

    // Cap against what is already attached/in flight, not just this batch —
    // drag & drop bypasses the canAttach() button guard, and every accepted
    // file now persists a reference row against the chat.
    const slots = Math.max(0, MAX_ATTACHMENTS - this.composerKeys().length);
    const uploads = allowed.slice(0, slots);
    if (!uploads.length) {
      done?.();
      return;
    }

    const added: Record<string, ChatFileVM> = {};
    for (const file of uploads) {
      const key = `up_${++this.uploadSeq}`;
      added[key] = {
        key,
        name: file.name,
        type: file.type || 'application/octet-stream',
        step: 'uploading',
        progress: 0,
      };
      this.uploadSources.set(key, file);
    }
    const keys = Object.keys(added);
    this.files.update(all => ({ ...all, ...added }));
    this.composerKeys.update(list => [...list, ...keys]);
    done?.();

    // v2 puts the owning entityId (= chatId) in the multipart body, so the
    // chat must exist before the first byte goes out. Chips are already on
    // screen as uploading meanwhile.
    this.ensureChatId().then(
      chatId => keys.forEach(key => this.startUpload(key, chatId)),
      error => {
        console.error('Could not create a chat for the attachment', error);
        this.attachmentFormatError.set(ATTACHMENT_UPLOAD_ERROR_MESSAGE);
        keys.forEach(key => this.failUpload(key));
      }
    );
  }

  private startUpload(key: string, chatId: string): void {
    const file = this.uploadSources.get(key);
    const vm = this.files()[key];
    if (!file || !vm) return; // removed before the chat was ready

    const formData = buildDocumentReferenceFormData({
      file,
      fileName: vm.name,
      entityType: DocumentReferenceEntityType.CHAT,
      entityId: chatId,
      attribute: DocumentAttribute.ALLO_ATTACHED_DOCUMENT,
      // PRIVATE, not v1's PUBLIC: DocumentServeService treats PUBLIC as "skip
      // the scope check entirely", and the BE's ChatDocumentReferenceEntityTask
      // forces PRIVATE for chat references anyway.
      accessLevel: AccessLevel.PRIVATE,
      description: vm.name,
    });

    const sub = this.chatDocStore
      .upload(formData, undefined, {
        onProgress: progress => this.patchFile(key, { progress }),
        // The chip shows the failure with "Try again"; the global handler would leave the page.
        skipErrorHandling: true,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: reference => {
          if (!reference) {
            this.failUpload(key);
            return;
          }
          const step = processingStep(reference.document);
          this.uploadSources.delete(key);
          this.patchFile(key, {
            referenceId: reference._id,
            documentId: reference.document._id,
            name: reference.document.originalFileName || vm.name,
            type: reference.document.contentType || vm.type,
            progress: 100,
            step,
            failure: step === 'failed' ? 'blocked' : undefined,
          });
          this.watchProcessing(key, reference._id);
          this.flushOutbox();
        },
        error: error => {
          console.error('Chat attachment upload failed', error);
          this.failUpload(key);
        },
      });
    // A synchronous upload has already finished, and may have started the watch that replaces it.
    if (!sub.closed) this.fileSubs.set(key, sub);
  }

  /** SD-3016: follows an uploaded file through the virus scan and text reading. */
  private watchProcessing(key: string, referenceId: string): void {
    const step = this.files()[key]?.step;
    if (step !== 'scanning' && step !== 'reading') return;
    const sub = this.scanGate
      .documentUpdates(this.chatDocService, referenceId)
      .pipe(
        map(processingStep),
        takeWhile(next => next === 'scanning' || next === 'reading', true),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: next =>
          this.patchFile(key, {
            step: next,
            failure: next === 'failed' ? 'blocked' : undefined,
          }),
        complete: () => {
          // The watch timed out mid-step. Allo has stopped waiting by then and says itself what it
          // couldn't read, so stop the spinner rather than leave it running.
          const left = this.files()[key]?.step;
          if (left === 'scanning' || left === 'reading') {
            this.patchFile(key, { step: 'ready' });
          }
        },
      });
    this.fileSubs.set(key, sub);
  }

  /**
   * SD-3016: an upload failed. The file stays visible, named, with "Try again". If its message was
   * already sent, the message goes without it and the file comes back to the composer.
   */
  private failUpload(key: string): void {
    if (!this.files()[key]) return; // removed meanwhile
    this.fileSubs.get(key)?.unsubscribe();
    this.fileSubs.delete(key);
    this.patchFile(key, { step: 'failed', failure: 'upload', progress: 0 });

    const owner = this.outbox.find(m => m.keys.includes(key));
    if (owner) {
      owner.keys = owner.keys.filter(k => k !== key);
      if (this.dropFileFromMessage(owner.messageId, key)) {
        this.composerKeys.update(keys => [...keys, key]);
      } else {
        this.forgetFile(key); // its chat isn't on screen any more
      }
    }
    this.flushOutbox();
  }

  private patchFile(key: string, patch: Partial<ChatFileVM>): void {
    this.files.update(all =>
      all[key] ? { ...all, [key]: { ...all[key], ...patch } } : all
    );
    this.cdr.markForCheck();
  }

  private forgetFile(key: string): void {
    this.fileSubs.get(key)?.unsubscribe();
    this.fileSubs.delete(key);
    this.uploadSources.delete(key);
    this.files.update(all => {
      const rest = { ...all };
      delete rest[key];
      return rest;
    });
  }

  private hasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  private clearAllPending(): void {
    this.attachmentNamesByMessageKey.clear();
    // SD-2526 Stage B: a chat switch clears the transient "still working…" hint and the
    // reconciled-turn marker — both belong to the chat we're leaving (the new chat's filtered
    // chatStream$ would otherwise never emit the event that clears a stranded hint).
    this.stillWorking.set(false);
    this.reconciledTs = null;
    this.pendingRoundBoundary = false;
    // SD-2155: switching chats invalidates any in-flight streaming bubble
    // — it belongs to the chat we just left. Drop it so the new chat's
    // history isn't polluted.
    const hadStream = this.streamingBubble() !== null;
    if (hadStream) {
      this.streamingBubble.set(null);
      this.streamDeltas.reset();
    }
    if (
      !hadStream &&
      !this.pendingQueue.length &&
      !this.pendingMessages().length
    ) {
      return;
    }
    this.pendingQueue = [];
    this.pendingMessages.set([]);
    this.cdr.markForCheck();
  }
}
